import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * The snapshot page, as a PDF.
 *
 * The typeset letter is one document the parent can keep; this is the other:
 * the page exactly as they read it, answer tables open and all. The browser
 * has already done the typesetting, so the PDF is built here - the element
 * is painted to a canvas, then sliced into A4 pages.
 */

const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;

/** How crisply the page is painted. 2 keeps table text readable when printed. */
const CAPTURE_SCALE = 2;
const JPEG_QUALITY = 0.92;

/**
 * Spots where a page may end: section, card, table-group and table-row
 * boundaries inside the captured element. A slice that ends mid-card cuts
 * the card in half on paper, so the closest boundary that fits wins.
 */
const BREAK_TARGETS =
  '.sl-hero, .sl-card, .sl-review, .sl-disclaimer, .sl-footer, ' +
  '.sl-tile, .sl-row, .sl-point, .sl-rev__group, .sl-rev__table tr, .sl-rev__section';

/** An ending this close to the page start would leave the page almost empty. */
const MIN_SLICE_FRACTION = 0.15;

/** Candidate break offsets, in CSS pixels below the element's top edge. */
const collectBreaks = (element: HTMLElement): number[] => {
  const rootTop = element.getBoundingClientRect().top;
  const offsets = new Set<number>([0]);
  element.querySelectorAll<HTMLElement>(BREAK_TARGETS).forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.height > 0) offsets.add(Math.round(rect.top - rootTop));
  });
  return Array.from(offsets).sort((a, b) => a - b);
};

/** One PDF page's worth of the painted canvas per call. */
const sliceIntoPages = (
  canvas: HTMLCanvasElement,
  pdf: jsPDF,
  breaksPx: number[]
): void => {
  const pxPerMm = canvas.width / PAGE_WIDTH_MM;
  const pageHeightPx = PAGE_HEIGHT_MM * pxPerMm;
  const minSlicePx = pageHeightPx * MIN_SLICE_FRACTION;

  let offsetY = 0;
  let first = true;
  while (offsetY < canvas.height) {
    const limit = offsetY + pageHeightPx;

    // The last boundary that still lands on this page. Sections taller than
    // a page simply slice wherever they fall - there is no kinder cut.
    let end = Math.min(limit, canvas.height);
    for (const boundary of breaksPx) {
      if (boundary <= offsetY + minSlicePx) continue;
      if (boundary > limit) break;
      end = boundary;
    }

    const sliceHeight = end - offsetY;
    const slice = document.createElement('canvas');
    slice.width = canvas.width;
    slice.height = sliceHeight;
    const ctx = slice.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, slice.width, slice.height);
    ctx.drawImage(canvas, 0, offsetY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

    if (!first) pdf.addPage();
    first = false;
    pdf.addImage(
      slice.toDataURL('image/jpeg', JPEG_QUALITY),
      'JPEG',
      0,
      0,
      PAGE_WIDTH_MM,
      sliceHeight / pxPerMm
    );
    offsetY = end;
  }
};

/** Paint ``element`` and return it as A4 PDF bytes. */
export const captureElementPdf = async (element: HTMLElement): Promise<Blob> => {
  // Boundaries are measured on the live DOM - call after the answer tables
  // have been forced open, or the breaks will be wrong for the capture.
  const breaksPx = collectBreaks(element).map((offset) => offset * CAPTURE_SCALE);
  const canvas = await html2canvas(element, {
    scale: CAPTURE_SCALE,
    backgroundColor: '#ffffff',
    useCORS: true,
    logging: false,
  });
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  sliceIntoPages(canvas, pdf, breaksPx);
  return pdf.output('blob');
};

/** A Blob as the base64 string the email endpoint expects. */
export const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
